import React from 'react';

/**
 * Compact 16×16 branded platform icon for inline use in feed metadata rows.
 * Uses real SVG brand marks where possible; falls back to a coloured initial.
 */
export function FeedPlatformIcon({ platform, className = '' }: { platform: string; className?: string }) {
  const base = `inline-flex items-center justify-center rounded-sm shrink-0 ${className}`;

  switch (platform) {
    case 'reddit':
      return (
        <span className={base} style={{ width: 16, height: 16, background: '#FF4500', borderRadius: 4 }}>
          {/* Reddit "alien" silhouette, simplified */}
          <svg width="11" height="11" viewBox="0 0 20 20" fill="white" aria-hidden>
            <circle cx="10" cy="10" r="10" fill="#FF4500"/>
            <path d="M16.67 10a1.46 1.46 0 0 0-2.47-1 7.12 7.12 0 0 0-3.85-1.23l.65-3.08 2.13.45a1 1 0 1 0 1-.97 1 1 0 0 0-.96.67l-2.38-.5a.27.27 0 0 0-.32.2l-.73 3.43a7.14 7.14 0 0 0-3.89 1.23 1.46 1.46 0 1 0-1.61 2.39 2.87 2.87 0 0 0 0 .44c0 2.24 2.61 4.06 5.83 4.06s5.83-1.82 5.83-4.06a2.87 2.87 0 0 0 0-.44 1.46 1.46 0 0 0 .68-1.59zM7.27 11a1 1 0 1 1 1 1 1 1 0 0 1-1-1zm5.58 2.65a3.54 3.54 0 0 1-2.85.77 3.54 3.54 0 0 1-2.85-.77.19.19 0 0 1 .27-.27 3.18 3.18 0 0 0 2.58.63 3.18 3.18 0 0 0 2.58-.63.19.19 0 0 1 .27.27zm-.14-1.65a1 1 0 1 1 1-1 1 1 0 0 1-1 1z" fill="white"/>
          </svg>
        </span>
      );

    case 'bluesky':
      return (
        <span className={base} style={{ width: 16, height: 16, background: '#0085FF', borderRadius: 4 }}>
          {/* Bluesky butterfly */}
          <svg width="11" height="11" viewBox="0 0 360 320" fill="white" aria-hidden>
            <path d="M180 141.964C163.699 110.262 119.308 51.1 78.0617 22.4162C38.4986 -4.98357 6.99959 3.68492 1.84544 38.0957C-2.38969 64.8466 5.39883 92.5286 14.5384 109.695C28.4406 135.716 58.8116 141.932 90.6648 145.707C61.7219 150.895 26.0579 166.677 9.13759 185.698C-21.4997 220.697 5.71001 262.137 46.5439 265.634C86.2128 269.056 113.676 244.965 136.214 222.874C154.359 205.11 170.067 183.916 180 165.017C189.933 183.916 205.641 205.11 223.786 222.874C246.324 244.965 273.787 269.056 313.456 265.634C354.29 262.137 381.5 220.697 350.862 185.698C333.942 166.677 298.278 150.895 269.335 145.707C301.188 141.932 331.559 135.716 345.462 109.695C354.601 92.5286 362.39 64.8466 358.155 38.0957C353 3.68492 321.501 -4.98357 281.938 22.4162C240.692 51.1 196.301 110.262 180 141.964Z"/>
          </svg>
        </span>
      );

    case 'youtube':
      return (
        <span className={base} style={{ width: 16, height: 16, background: '#FF0000', borderRadius: 4 }}>
          {/* YouTube play button */}
          <svg width="11" height="11" viewBox="0 0 24 24" fill="white" aria-hidden>
            <path d="M23.5 6.19a3.02 3.02 0 0 0-2.12-2.14C19.54 3.5 12 3.5 12 3.5s-7.54 0-9.38.55A3.02 3.02 0 0 0 .5 6.19C0 8.04 0 12 0 12s0 3.96.5 5.81a3.02 3.02 0 0 0 2.12 2.14C4.46 20.5 12 20.5 12 20.5s7.54 0 9.38-.55a3.02 3.02 0 0 0 2.12-2.14C24 15.96 24 12 24 12s0-3.96-.5-5.81zM9.75 15.5v-7l6.5 3.5-6.5 3.5z"/>
          </svg>
        </span>
      );

    case 'twitter':
      return (
        <span className={base} style={{ width: 16, height: 16, background: '#000000', borderRadius: 4 }}>
          {/* X logo */}
          <svg width="9" height="9" viewBox="0 0 24 24" fill="white" aria-hidden>
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
        </span>
      );

    case 'rss':
      return (
        <span className={base} style={{ width: 16, height: 16, background: '#F26522', borderRadius: 4 }}>
          {/* RSS waves */}
          <svg width="10" height="10" viewBox="0 0 24 24" fill="white" aria-hidden>
            <circle cx="6.18" cy="17.82" r="2.18"/>
            <path d="M4 4.44v2.83c7.03 0 12.73 5.7 12.73 12.73h2.83c0-8.59-6.97-15.56-15.56-15.56zm0 5.66v2.83c3.9 0 7.07 3.17 7.07 7.07h2.83c0-5.47-4.43-9.9-9.9-9.9z"/>
          </svg>
        </span>
      );

    default:
      return (
        <span
          className={base}
          style={{ width: 16, height: 16, background: '#6B7280', borderRadius: 4, fontSize: 9, color: 'white', fontWeight: 700, letterSpacing: -0.5 }}
        >
          {platform.charAt(0).toUpperCase()}
        </span>
      );
  }
}

export default FeedPlatformIcon;
